import {
  type AroundATimeState,
  autocompleteClasses,
  Box,
  InputAdornment,
  inputAdornmentClasses,
  MenuItem,
  menuItemClasses,
  SELECT_DEFAULT_RENDER_OPTION,
  Select,
  styled,
  type TimeRangeSelectionMode,
  useBreakpointDown,
  useCopyToClipboard,
} from '@chronosphereio/chrono-ui';
import {
  areTimeRangeInputValuesEqual,
  DATE_FORMAT,
  formatWithTimeZone,
  isTimeRangeInputValueAbsolute,
  TimeRangeInputValue,
  useFeatureFlags,
} from '@chronosphereio/core';
import { isSameDay } from 'date-fns';
import { ClockOutline } from 'mdi-material-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getResponsiveTimeRangeSelectMinWidth } from '@/components/utils/responsiveTimeRangeUtils';
import { NamedTimeOption } from '@/core';
import { useUserTimeFormat } from '@/plugin-system/runtime/useUserTimeFormat';
import { TimeZone } from '../../context';
import {
  absoluteTimeRangeDatesHaveCorrectOrder,
  formatTimeRangeInputValue,
  parseTimeRange,
  timeRangeHasZeroSeconds,
  useTimeRangeSelectLayout,
} from '../utils';
import {
  CUSTOM_OPTION_KEY,
  ERROR_OPTION_KEY,
  ErrorOptionKey,
  filterTimeRangeOptions,
  getErrorOptionLabels,
  getTimeRangeSelectOptions,
  TimeOption,
  TimeRangeSelectOption,
} from '../utils/options';
import { useTimeRangeLastLoadedTimePicker } from '../utils/useTimeRangeLastLoadedTimePicker';
import { useTimeZoneDetails } from '../utils/useTimeZoneDetails';
import { TimeRangeSelectEndAdornment } from './TimeRangeSelectEndAdornment';
import { TimeRangeSelectPanel } from './TimeRangeSelectPanel';

interface TimeRangeSelectProps extends TimeZone {
  onChange: (newTimeRange: TimeRangeInputValue) => void;
  value: TimeRangeInputValue;
  timeOptions?: NamedTimeOption | TimeOption[];
  minDate?: Date;
  maxDate?: Date;
  enableResponsiveWidth?: boolean;
  disableTimeZoneOverride?: boolean;
}

export const TimeRangeSelect = ({
  onChange,
  value,
  timeOptions: initTimeOptionsProp,
  timeZone,
  setTimeZone,
  userTimeZone,
  minDate,
  maxDate,
  enableResponsiveWidth,
  disableTimeZoneOverride,
}: TimeRangeSelectProps) => {
  const [timeRangeSelectionMode, setTimeRangeSelectionMode] = useState<TimeRangeSelectionMode>('fromTo');
  const [duration, setDuration] = useState('');
  const [center, setCenter] = useState('');
  const aroundATime = useMemo<AroundATimeState>(
    () => ({ center, duration, setCenter, setDuration }),
    [center, duration]
  );
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [inputError, setInputError] = useState<false | ErrorOptionKey>(false);
  const [isMenuHighlightedByKeyboard, setIsMenuHighlightedByKeyboard] = useState(false);

  const timeFormat = useUserTimeFormat();
  const selectRef = useRef<HTMLDivElement>(null);
  const { panwRebrandStubYear } = useFeatureFlags();
  const errorOptionLabels = useMemo(() => getErrorOptionLabels(panwRebrandStubYear), [panwRebrandStubYear]);
  const isMobile = useBreakpointDown('sm', { breakpointSource: 'viewport' });
  const copy = useCopyToClipboard();

  function handleCopyTimeRange() {
    const formatted = formatTimeRangeInputValue({ timeRange: value, timeFormat, timeZone });
    void copy(formatted, 'Copied time range');
  }

  const { timeOptions, longestDisplayValue } = useMemo(() => {
    return getTimeRangeSelectOptions({
      initTimeOptions: initTimeOptionsProp ?? 'default',
      timeFormat,
      timeZone,
    });
  }, [initTimeOptionsProp, timeFormat, timeZone]);

  const selectTimeOptions = useMemo(() => {
    if (!inputError) {
      return timeOptions;
    }

    // Inject the input error as an option, when needed.
    const errorOption: TimeRangeSelectOption = {
      key: ERROR_OPTION_KEY,
      label: errorOptionLabels[inputError],
      value: ERROR_OPTION_KEY,
    };
    return [errorOption].concat(timeOptions);
  }, [inputError, timeOptions, errorOptionLabels]);

  const selectValue = useMemo(() => {
    // Attempt to get value from timeOptions array.
    const matchingBasicOption = selectTimeOptions.find((timeOption) => {
      if (typeof timeOption.value === 'string') {
        // Skip custom and error options
        return false;
      }
      return areTimeRangeInputValuesEqual(timeOption.value, value);
    });
    if (matchingBasicOption) {
      return matchingBasicOption;
    }

    let omitSeconds = false;
    // omit seconds when both the start and end dates end in 00 seconds
    if (isTimeRangeInputValueAbsolute(value)) {
      omitSeconds = timeRangeHasZeroSeconds(value);
    }

    // If current option is not in array (this occurs when a custom time range is set), use a
    // custom value that shows the actual time range in the chronosphere format.
    let formattedValue = formatTimeRangeInputValue({ timeRange: value, timeFormat, timeZone, omitSeconds });
    if (isTimeRangeInputValueAbsolute(value) && isSameDay(value.start, value.end)) {
      // Remove the second date (end date) from same-day ranges
      const formattedEndDate = formatWithTimeZone(value.end, DATE_FORMAT, timeZone);
      const endDateIndex = formattedValue.lastIndexOf(formattedEndDate);
      if (endDateIndex !== -1) {
        formattedValue =
          formattedValue.slice(0, endDateIndex) + formattedValue.slice(endDateIndex + formattedEndDate.length);
        // Clean up extra spacing/commas around the dash
        formattedValue = formattedValue.replace(/\s+-\s+,?\s*/, ' - ');
      }
    }
    return {
      key: CUSTOM_OPTION_KEY,
      label: formattedValue,
      value: value,
    };
  }, [selectTimeOptions, timeFormat, timeZone, value]);

  // Resets some interaction-related states that should go back to their defaults when the select
  // "resets" in cases like closing the dropdown and changing the selected item.
  function resetInteractionStatus() {
    setInputError(false);
    setIsMenuHighlightedByKeyboard(false);
  }

  useEffect(() => {
    if (selectValue.label !== inputValue) {
      // When selectValue.label changes, the selected item has been changed through props.
      // Reset the input value and clear errors. This useEffect is needed because the default
      // "reset" behavior in the `Select` `onInputChange` breaks some interaction patterns for
      // pasting.
      resetInteractionStatus();
      setInputValue(selectValue.label);
    }

    // Intentionally only want this to run when the selectValue.label changes. The inputValue
    // changing is handled by the `Select` props.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectValue.label]);

  const lastLoaded = useTimeRangeLastLoadedTimePicker({
    timeRange: value,
  });

  const timeZoneDetails = useTimeZoneDetails({
    timeZone,
  });

  const lastLoadedMessage = (!isMobile && lastLoaded.lastLoadedMessage) || '';
  const { selectWidth, listboxHeight, listboxWidth } = useTimeRangeSelectLayout({
    inputValue,
    longestDisplayValue,
    lastLoadedValue: lastLoadedMessage,
    timeZoneValue: timeZoneDetails.shortName ?? '',
    showInputError: !!inputError,
    optionsCount: selectTimeOptions.length,
  });

  const responsiveMinWidth = getResponsiveTimeRangeSelectMinWidth(value);

  function handleSelectChange(newTimeRange: TimeRangeInputValue) {
    resetInteractionStatus();
    onChange(newTimeRange);
  }

  // Handles input changes from a user manually typing/pasting a raw text input.
  function handleManualInputChange(text: string) {
    const potentialNewDate = parseTimeRange(text, timeZone);

    if (!potentialNewDate) {
      // If the user's input does not lead to a parsed date, show an error message and
      // open the panel to show the error.
      const error: ErrorOptionKey = timeFormat === '24h' ? 'UNSUPPORTED_24H' : 'UNSUPPORTED_12H';

      setInputError(error);
      setIsOpen(true);
    } else if (
      isTimeRangeInputValueAbsolute(potentialNewDate) &&
      !absoluteTimeRangeDatesHaveCorrectOrder(potentialNewDate)
    ) {
      // If the user's input leads to a parsed absolute date where the start/end dates are in the
      // wrong order, show an error message and open the panel to show the error.
      setInputError('INVALID_ORDER');
      setIsOpen(true);
    } else {
      handleSelectChange(potentialNewDate);
      // Close the panel when successful because the user no longer needs to see it.
      setIsOpen(false);
    }
  }

  return (
    <>
      <SelectContainer
        ref={selectRef}
        // adding for Chameleon to be able to target the select
        data-chmln={'new-date-time-picker-select'}
      >
        <Select
          error={!!inputError}
          disableClearable
          value={selectValue}
          inputValue={inputValue}
          open={false}
          onInputChange={(e, value, reason) => {
            if (reason === 'reset' && inputError) {
              // Do not reset the input when we're trying to show an error from typed input. Otherwise
              // the input loses the context for what they typed, which could be annoying if the issue
              // was a small typo. Unfortunately, we cannot tell the difference between this being
              // triggered by the paste/enter events OR it being caused by the props changing, so we
              // handle the "props changing" case in a useEffect above.
              return;
            }
            setInputValue(value);
          }}
          options={selectTimeOptions}
          isOptionEqualToValue={(option, value) => {
            return option.key === value.key;
          }}
          filterOptions={filterTimeRangeOptions}
          onHighlightChange={(e, value, reason) => {
            if (reason === 'keyboard') {
              // Keep track of keyboard-based highlighting, so that we can differentiate when the
              // "enter" key is intended to submit changes in the text input and when it is intended
              // to select an item in the dropdown.
              setIsMenuHighlightedByKeyboard(true);
            }
          }}
          dangerouslyRenderOption={(props, option, state) => {
            if (option.value === ERROR_OPTION_KEY) {
              // Custom rendering for faux option used to provide an error message in the time picker
              // because we cannot guarantee space to show the error message outside the time picker.
              return (
                <ErrorMenuItem {...props} isDisabled key={option.key} description={option.description}>
                  {option.label}
                </ErrorMenuItem>
              );
            }
            // Otherwise, use default option rendering.
            return SELECT_DEFAULT_RENDER_OPTION(props, option, state);
          }}
          onChange={(e, value) => {
            if (value?.value === ERROR_OPTION_KEY) {
              // do nothing if the user clicks on the error.
              return;
            }

            resetInteractionStatus();
            if (value?.value && typeof value.value !== 'string') {
              handleSelectChange(value.value);
            }
          }}
          inputProps={{
            // Needs to be set on `inputProps` because MUI doesn't pass it along
            // if set on `InputProps`.
            'aria-label': `Select time range. Currently set to ${selectValue.label}`,
            readOnly: true,
          }}
          InputProps={(params) => {
            return {
              ...params,
              startAdornment: (
                <InputAdornment position="start" onClick={() => setIsOpen(true)}>
                  <ClockOutline
                    sx={{
                      width: '18px',
                      height: '18px',
                      color: (theme) => theme.palette.text.primary,
                    }}
                  />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  <TimeRangeSelectEndAdornment
                    lastLoadedMessage={lastLoadedMessage}
                    userTimeZone={userTimeZone}
                    timeZoneDetails={timeZoneDetails}
                    onLastLoadedMessageClick={() => setIsOpen(true)}
                    onTimeZoneClick={() => setIsOpen(true)}
                    disableTimeZoneOverride={disableTimeZoneOverride}
                  />
                  <Box onClick={() => setIsOpen(true)}>{params.endAdornment}</Box>
                </>
              ),
              onClick: () => setIsOpen(true),
              onKeyDown: (e) => {
                if (e.key === 'Enter' && !isOpen) {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsOpen(true);
                } else if (e.key === 'Enter' && !isMenuHighlightedByKeyboard) {
                  // Prevent other events from triggering when hitting enter. This avoids
                  // some cases where the MUI Autocomplete would try to "select" the active
                  // item in the dropdown.
                  e.stopPropagation();

                  const newValue = e.currentTarget.value;
                  handleManualInputChange(newValue);
                }

                // Handle arrow keys to open the panel (since MUI's default behavior is disabled)
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault(); // Prevent cursor movement in input
                  setIsOpen(true);
                }

                // Handle Ctrl/Cmd+C to copy when panel is closed
                if (!isOpen && (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
                  e.preventDefault();
                  handleCopyTimeRange();
                }
              },
              onPaste: (e) => {
                // Preventing default behavior avoids a weird bug where the pasted value
                // gets duplicated (e.g. pasting "1 hour" shows as "1 hour1 hour").
                // I wasn't able to find the exact line of MUI code that causes this, but
                // this solution stops whatever part of the code is causing it.
                e.preventDefault();
                const pastedValue = e.clipboardData.getData('text/plain');
                setInputValue(pastedValue);
                handleManualInputChange(pastedValue);
              },
            };
          }}
          listboxHeight={`${listboxHeight}px`}
          // Setting a max height that is higher than the default 40vh from MUI to reduce the
          // likelihood of a scrollbar showing, while also avoiding the listbox being too tall on
          // smaller screens. This value should show all of the default options on a screen that is
          // 800px or larger when the time picker is near the top of the UI.
          listboxMaxHeight="60vh"
          popperWidth={`${listboxWidth}px`}
          sx={(theme) => ({
            // When responsive width is enabled, we use a clamp to try to min/max the width of the
            // select with the given space. This works alongside a flex-basis in the `TimeRangeControls`.
            // On mobile we shrink down since we're not displaying a time in it anymore.
            ...(!isMobile
              ? {
                  width: enableResponsiveWidth ? `clamp(${responsiveMinWidth}px, ${selectWidth}px, 100%)` : selectWidth,
                }
              : {}),
            [`.${inputAdornmentClasses.positionStart}`]: {
              // Adjusting to more closely match spacing between icon and text in mocks.
              marginRight: isMobile ? theme.spacing(-1) : 0.25,
            },
            [`.${autocompleteClasses.inputRoot}`]: {
              // Minimizes a bug where if the measurements aren't quite right, the end adornment gets
              // wrapped to another line and looks very strange. This mostly happens in safari where
              // we have some measurement bugs.
              flexWrap: 'nowrap',
              cursor: 'pointer',
            },
            [`.${autocompleteClasses.input}`]: {
              cursor: 'pointer',
              // make the input not look typeable
              '&::selection': {
                backgroundColor: 'transparent',
              },
              userSelect: 'none',
            },
            // Hide input text on mobile
            ...(isMobile && {
              [`& .${autocompleteClasses.input}`]: {
                visibility: 'hidden',
                minWidth: '0 !important',
              },
            }),
          })}
        />
      </SelectContainer>
      <TimeRangeSelectPanel
        aroundATime={aroundATime}
        timeRangeSelectionMode={timeRangeSelectionMode}
        setTimeRangeSelectionMode={setTimeRangeSelectionMode}
        open={isOpen}
        anchorEl={selectRef.current}
        onClose={() => {
          setIsOpen(false);
          resetInteractionStatus();
        }}
        onChange={onChange}
        timeOptions={initTimeOptionsProp}
        timeZone={timeZone}
        timeFormat={timeFormat}
        minDate={minDate}
        maxDate={maxDate}
        inputError={inputError}
        errorInputValue={inputError ? inputValue : undefined}
        userTimeZone={userTimeZone}
        setTimeZone={setTimeZone}
        disableTimeZoneOverride={disableTimeZoneOverride}
        lastLoadedMessage={lastLoaded.lastLoadedMessage}
        onInputError={setInputError}
      />
    </>
  );
};

const SelectContainer = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'flex-end',
});

const ErrorMenuItem = styled(MenuItem, {
  shouldForwardProp: (prop) => prop !== 'isDisabled',
})<{ isDisabled?: boolean }>(({ theme }) => {
  return {
    textWrap: 'wrap',
    wordBreak: 'break-word',
    color: theme.palette.error.main,
    [`&.${menuItemClasses.root}.${menuItemClasses.disabled}`]: {
      opacity: 1,
    },
  };
});
